
(function () {
  if (customElements.get("hotspot-section")) return;

class HotspotSection extends HTMLElement {
  connectedCallback() {
    this.pins = Array.from(this.querySelectorAll(".hs_pin"));
    this.items = Array.from(this.querySelectorAll(".hs_item"));
    this.media = this.querySelector(".hs_media");

    if (!this.pins.length || !this.items.length) return;

    this.activeIndex = this.pins.findIndex((pin) => pin.classList.contains("is-active"));
    if (this.activeIndex === -1) this.activeIndex = 0;

    this.pins.forEach((pin) => {
      pin.addEventListener("click", () => {
        const index = Number(pin.dataset.index);
        this.setActive(index);
        this.scrollToStep(index);
      });
    });


    this.items.forEach((item, i) => this.setItemInteractive(item, i === this.activeIndex));

    this.setupReveal();
    this.setupPin();
  }

  disconnectedCallback() {
    this.teardownPin();
  }

  /* The active card  */
  setItemInteractive(item, isActive) {
    item.querySelectorAll("a").forEach((link) => {
      const isThumb = link.classList.contains("hs_item_media");

      link.tabIndex = isActive && !isThumb ? 0 : -1;

      if (isActive) {
        link.removeAttribute("aria-disabled");
      } else {
        link.setAttribute("aria-disabled", "true");
      }
    });
  }

  setActive(index) {
    if (index === this.activeIndex) return;
    this.activeIndex = index;

    this.pins.forEach((pin, i) => {
      const isActive = i === index;
      pin.classList.toggle("is-active", isActive);
      pin.setAttribute("aria-pressed", String(isActive));
    });

    this.items.forEach((item, i) => {
      const isActive = i === index;
      item.classList.toggle("is-active", isActive);
      this.setItemInteractive(item, isActive);
    });
  }

  setupReveal() {
    if (!this.media) return;

    if (!("IntersectionObserver" in window)) {
      this.media.classList.add("is-in-view");
      this.markRevealed();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.media.classList.add("is-in-view");
          this.markRevealed();
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.2, rootMargin: "0px 0px -40px 0px" },
    );

    observer.observe(this.media);
  }


  markRevealed() {
    clearTimeout(this.revealTimer);
    this.revealTimer = setTimeout(() => {
      this.media.classList.add("is-revealed");
    }, this.pins.length * 120 + 500);
  }

  /* ---------- pinned scroll (desktop) ---------- */

  setupPin() {
    this.track = this.closest(".hs_scroll");
    this.sticky = this.closest(".hs_sticky");

    if (!this.track || !this.sticky || this.pins.length < 2) return;
    if (this.track.dataset.scrollEffect === "false") return;

    this.desktopQuery = window.matchMedia("(min-width: 990px)");
    this.motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    this.onPinScroll = () => {
      if (this.pinFrame) return;
      this.pinFrame = requestAnimationFrame(() => {
        this.pinFrame = null;
        this.updateFromScroll();
      });
    };

    this.onPinResize = () => this.measurePin();
    this.onModeChange = () => this.refreshPin();

    if (this.desktopQuery.addEventListener) {
      this.desktopQuery.addEventListener("change", this.onModeChange);
      this.motionQuery.addEventListener("change", this.onModeChange);
    }

    if ("ResizeObserver" in window) {
      this.pinObserver = new ResizeObserver(() => this.measurePin());
      this.pinObserver.observe(this.sticky);
    }

    this.refreshPin();
  }

  refreshPin() {
    const enable = this.desktopQuery.matches && !this.motionQuery.matches;

    if (enable === this.pinEnabled) {
      if (enable) this.measurePin();
      return;
    }

    this.pinEnabled = enable;

    if (enable) {
      this.track.classList.add("is-pinned");
      window.addEventListener("scroll", this.onPinScroll, { passive: true });
      window.addEventListener("resize", this.onPinResize);
      this.measurePin();
    } else {
      this.track.classList.remove("is-pinned");
      this.track.style.removeProperty("--hs-track-height");
      this.track.style.removeProperty("--hs-sticky-top");
      window.removeEventListener("scroll", this.onPinScroll);
      window.removeEventListener("resize", this.onPinResize);
    }
  }

  measurePin() {
    if (!this.pinEnabled) return;

    const viewport = window.innerHeight;
    const stickyHeight = this.sticky.offsetHeight;

    /* Too tall to pin  */
    if (stickyHeight > viewport) {
      this.track.classList.remove("is-pinned");
      this.track.style.removeProperty("--hs-track-height");
      this.track.style.removeProperty("--hs-sticky-top");
      this.pinRange = 0;
      return;
    }

    this.track.classList.add("is-pinned");

    this.stickyTop = Math.round((viewport - stickyHeight) / 2);
    this.pinRange = Math.round(viewport * 0.6) * this.pins.length;

    this.track.style.setProperty("--hs-sticky-top", this.stickyTop + "px");
    this.track.style.setProperty("--hs-track-height", stickyHeight + this.pinRange + "px");

    this.updateFromScroll();
  }

  updateFromScroll() {
    if (!this.pinEnabled || !this.pinRange) return;

    const scrolled = this.stickyTop - this.track.getBoundingClientRect().top;
    const progress = Math.min(Math.max(scrolled / this.pinRange, 0), 0.999999);

    this.setActive(Math.floor(progress * this.pins.length));
  }

  /* Clicking a pin */
  scrollToStep(index) {
    if (!this.pinEnabled || !this.pinRange) return;

    const step = this.pinRange / this.pins.length;
    const target =
      this.track.getBoundingClientRect().top +
      window.scrollY -
      this.stickyTop +
      step * index +
      step / 2;

    window.scrollTo({ top: Math.round(target), behavior: "smooth" });
  }

  teardownPin() {
    clearTimeout(this.revealTimer);
    if (this.pinFrame) cancelAnimationFrame(this.pinFrame);
    if (this.pinObserver) this.pinObserver.disconnect();
    if (this.onPinScroll) window.removeEventListener("scroll", this.onPinScroll);
    if (this.onPinResize) window.removeEventListener("resize", this.onPinResize);
    if (this.desktopQuery && this.desktopQuery.removeEventListener) {
      this.desktopQuery.removeEventListener("change", this.onModeChange);
      this.motionQuery.removeEventListener("change", this.onModeChange);
    }
    this.pinEnabled = false;
  }
}

customElements.define("hotspot-section", HotspotSection);
})();
